import { Search } from '@mui/icons-material'
import { Box, CircularProgress, InputAdornment, MenuItem, Stack, TextField, Typography, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import type { BillingAsset, BillingAssetType } from 'src/generated-types/customer-license'
import { AssetBillingTable } from './AssetBillingTable'

type DetailedBreakdownSectionProps = {
  search: string
  onSearchChange: (v: string) => void
  assetType: BillingAssetType | 'all'
  onAssetTypeChange: (v: BillingAssetType | 'all') => void
  rows: BillingAsset[]
  billingPeriodFrom: Date | null
  billingPeriodTo: Date | null
  facilitiesLoading?: boolean
  onViewLocalityActivityLog?: (asset: BillingAsset) => void
}

export const DetailedBreakdownSection = ({
  search,
  onSearchChange,
  assetType,
  onAssetTypeChange,
  rows,
  billingPeriodFrom,
  billingPeriodTo,
  facilitiesLoading,
  onViewLocalityActivityLog,
}: DetailedBreakdownSectionProps) => {
  const { t } = useTranslation('admin-license')

  return (
    <DetailedBreakdownSectionRoot>
      <Typography variant="subtitle1" fontWeight={600}>
        {t('detailedBreakdown')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1.5} flexWrap="wrap">
        <TextField
          size="small"
          placeholder={t('searchBreakdown')}
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
          inputProps={{ 'aria-label': t('searchBreakdown') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" aria-hidden />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label={t('assetType')}
          value={assetType}
          onChange={e => onAssetTypeChange(e.target.value as BillingAssetType | 'all')}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">{t('allTypes')}</MenuItem>
          <MenuItem value="locality">{t('locality')}</MenuItem>
          <MenuItem value="serviceStation">{t('serviceStation')}</MenuItem>
          <MenuItem value="storage">{t('storage')}</MenuItem>
        </TextField>
        <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
          {t('recordsCount', { count: rows.length })}
        </Typography>
      </Stack>
      <DetailedBreakdownTableShell>
        {facilitiesLoading ? (
          <Box
            component="div"
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label={String(t('loadingFacilities'))}
          >
            <CircularProgress size={28} />
          </Box>
        ) : (
          <AssetBillingTable
            rows={rows}
            periodFrom={billingPeriodFrom}
            periodTo={billingPeriodTo}
            onViewLocalityActivityLog={onViewLocalityActivityLog}
          />
        )}
      </DetailedBreakdownTableShell>
    </DetailedBreakdownSectionRoot>
  )
}

const DetailedBreakdownSectionRoot = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  padding: theme.spacing(2),
  paddingTop: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}))

const DetailedBreakdownTableShell = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 200,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  position: 'relative',
}))
